// components/AlertLayer.jsx
// Layer di alert da aggiungere alla mappa esistente

import React, { useState, useEffect, useMemo } from 'react';
import { Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { fetchAlerts } from '../services/AlertService';

// ============================================
// CONFIGURAZIONE TIPI ALERT
// ============================================

export const ALERT_TYPES = {
    weather: {
        label: 'Temporale',
        emoji: '⛈️',
        color: '#FF9800'
    },
    avalanche: {
        label: 'Valanghe',
        emoji: '❄️',
        color: '#2196F3'
    },
    snow: {
        label: 'Neve',
        emoji: '🌨️',
        color: '#42A5F5'
    },
    flood: {
        label: 'Fiume',
        emoji: '🌊',
        color: '#03A9F4'
    },
    wind: {
        label: 'Vento',
        emoji: '💨',
        color: '#9E9E9E'
    },
    landslide: {
        label: 'Frana',
        emoji: '⚠️',
        color: '#795548'
    }
};

export const SEVERITY_LEVELS = {
    low: { label: 'Basso', color: '#4CAF50', priority: 1 },
    medium: { label: 'Medio', color: '#FF9800', priority: 2 },
    high: { label: 'Alto', color: '#FF5722', priority: 3 },
    extreme: { label: 'Estremo', color: '#B71C1C', priority: 4 }
};

// ============================================
// STILE CSS DA INIETTARE
// ============================================

const ALERT_STYLES = `
  @keyframes pulse-alert {
    0%, 100% { transform: scale(1); opacity: 0.3; }
    50% { transform: scale(1.2); opacity: 0.5; }
  }
  
  .custom-alert-icon {
    background: none !important;
    border: none !important;
  }
  
  .alert-popup {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
`;

// Inietta stili una volta
if (typeof document !== 'undefined') {
    const styleId = 'alert-layer-styles';
    if (!document.getElementById(styleId)) {
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = ALERT_STYLES;
        document.head.appendChild(style);
    }
}

// ============================================
// ICONA CUSTOM ALERT
// ============================================

function createAlertIcon(type, severity) {
    const alertInfo = ALERT_TYPES[type];
    const severityInfo = SEVERITY_LEVELS[severity];

    const html = `
    <div style="
      position: relative;
      width: 40px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        position: absolute;
        width: 100%;
        height: 100%;
        background: ${severityInfo.color};
        border-radius: 50%;
        opacity: 0.3;
        animation: pulse-alert 2s infinite;
      "></div>
      <div style="
        position: relative;
        font-size: 24px;
        background: white;
        border-radius: 50%;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border: 3px solid ${severityInfo.color};
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      ">
        ${alertInfo.emoji}
      </div>
    </div>
  `;

    return L.divIcon({
        html,
        className: 'custom-alert-icon',
        iconSize: [40, 40],
        iconAnchor: [20, 20],
        popupAnchor: [0, -20]
    });
}

// ============================================
// SINGOLO MARKER ALERT
// ============================================

function AlertMarker({ alert }) {
    const icon = useMemo(() =>
        createAlertIcon(alert.type, alert.severity),
        [alert.type, alert.severity]
    );

    const alertInfo = ALERT_TYPES[alert.type];
    const severityInfo = SEVERITY_LEVELS[alert.severity];

    const timeLeft = Math.round((alert.validUntil - new Date()) / 3600000);

    return (
        <Marker position={alert.coords} icon={icon}>
            <Popup maxWidth={300} className="alert-popup">
                <div style={{ padding: '8px' }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginBottom: '12px'
                    }}>
                        <span style={{ fontSize: '28px' }}>{alertInfo.emoji}</span>
                        <div style={{ flex: 1 }}>
                            <div style={{
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#333'
                            }}>
                                {alert.message}
                            </div>
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                marginTop: '2px'
                            }}>
                                {alert.area}
                            </div>
                        </div>
                    </div>

                    <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        background: severityInfo.color,
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        marginBottom: '12px'
                    }}>
                        Livello: {severityInfo.label}
                    </div>

                    <div style={{
                        fontSize: '14px',
                        color: '#555',
                        marginBottom: '12px',
                        lineHeight: '1.5'
                    }}>
                        {alert.details}
                    </div>

                    <div style={{
                        borderTop: '1px solid #ddd',
                        paddingTop: '8px',
                        fontSize: '12px',
                        color: '#666'
                    }}>
                        <div style={{ marginBottom: '4px' }}>
                            ⏰ Valido per {timeLeft}h
                        </div>
                        <div>
                            📡 {alert.source}
                        </div>
                    </div>
                </div>
            </Popup>
        </Marker>
    );
}

// ============================================
// BADGE CONTATORE ALERT
// ============================================

function AlertBadge({ count, severity }) {
    if (count === 0) return null;

    const severityInfo = SEVERITY_LEVELS[severity];

    return (
        <div style={{
            position: 'absolute',
            top: '80px',
            left: '10px',
            background: 'white',
            padding: '8px 16px',
            borderRadius: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }}>
            <div style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: severityInfo.color,
                animation: 'pulse-alert 2s infinite'
            }} />
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                {count} Alert {severityInfo.label}
            </span>
        </div>
    );
}

// ============================================
// LAYER PRINCIPALE
// ============================================

export default function AlertLayer({
    enabled = true,
    showBadge = true,
    typeFilters = Object.keys(ALERT_TYPES),
    severityFilters = Object.keys(SEVERITY_LEVELS)
}) {
    const map = useMap();
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch alerts quando la mappa si muove o all'inizio
    useEffect(() => {
        if (!enabled) return;

        const loadAlerts = async () => {
            try {
                setLoading(true);
                const bounds = map.getBounds();
                const boundsObj = {
                    north: bounds.getNorth(),
                    south: bounds.getSouth(),
                    east: bounds.getEast(),
                    west: bounds.getWest()
                };

                const fetchedAlerts = await fetchAlerts(boundsObj);
                setAlerts(fetchedAlerts);
            } catch (err) {
                console.error('Error loading alerts:', err);
            } finally {
                setLoading(false);
            }
        };

        loadAlerts();

        // Ricarica quando la mappa si muove (con debounce)
        let moveTimeout;
        const handleMoveEnd = () => {
            clearTimeout(moveTimeout);
            moveTimeout = setTimeout(loadAlerts, 1000);
        };

        map.on('moveend', handleMoveEnd);

        // Ricarica ogni 30 minuti
        const refreshInterval = setInterval(loadAlerts, 30 * 60 * 1000);

        return () => {
            map.off('moveend', handleMoveEnd);
            clearInterval(refreshInterval);
            clearTimeout(moveTimeout);
        };
    }, [map, enabled]);

    // Filtra alert in base ai filtri attivi
    const filteredAlerts = useMemo(() => {
        return alerts.filter(alert =>
            typeFilters.includes(alert.type) &&
            severityFilters.includes(alert.severity)
        );
    }, [alerts, typeFilters, severityFilters]);

    // Trova l'alert con severity più alta
    const highestSeverity = useMemo(() => {
        if (filteredAlerts.length === 0) return null;

        return filteredAlerts.reduce((highest, alert) => {
            const currentPriority = SEVERITY_LEVELS[alert.severity].priority;
            const highestPriority = SEVERITY_LEVELS[highest].priority;
            return currentPriority > highestPriority ? alert.severity : highest;
        }, 'low');
    }, [filteredAlerts]);

    if (!enabled) return null;

    if (loading && alerts.length === 0) {
        return (
            <div style={{
                position: 'absolute',
                top: '80px',
                left: '10px',
                background: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 1000,
                fontSize: '14px'
            }}>
                🔄 Caricamento alert...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                position: 'absolute',
                top: '80px',
                left: '10px',
                background: '#ffebee',
                padding: '8px 16px',
                borderRadius: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                zIndex: 1000,
                fontSize: '14px',
                color: '#c62828'
            }}>
                ⚠️ Errore caricamento alert
            </div>
        );
    }

    return (
        <>
            {/* Markers */}
            {filteredAlerts.map(alert => (
                <AlertMarker key={alert.id} alert={alert} />
            ))}

            {/* Badge contatore */}
            {showBadge && highestSeverity && (
                <AlertBadge
                    count={filteredAlerts.length}
                    severity={highestSeverity}
                />
            )}
        </>
    );
}

// ============================================
// HOOK PER CONTROLLARE ALERT
// ============================================

/**
 * Hook per gestire lo stato degli alert
 * Usa questo nel componente padre per controllare filtri
 */
export function useAlertFilters() {
    const [typeFilters, setTypeFilters] = useState(Object.keys(ALERT_TYPES));
    const [severityFilters, setSeverityFilters] = useState(Object.keys(SEVERITY_LEVELS));
    const [enabled, setEnabled] = useState(true);

    const toggleType = (type) => {
        setTypeFilters(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        );
    };

    const toggleSeverity = (severity) => {
        setSeverityFilters(prev =>
            prev.includes(severity)
                ? prev.filter(s => s !== severity)
                : [...prev, severity]
        );
    };

    const reset = () => {
        setTypeFilters(Object.keys(ALERT_TYPES));
        setSeverityFilters(Object.keys(SEVERITY_LEVELS));
    };

    return {
        typeFilters,
        severityFilters,
        enabled,
        setEnabled,
        toggleType,
        toggleSeverity,
        reset
    };
}